import { InviteCheckResult } from '@/api';
import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAcceptInvite, useCheckInviteStatus } from '@/hooks/useAuthMutations';
import { useUser } from '@/hooks/useUser';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building, CheckCircle, Lock, Mail, User } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { getAxiosErrorCode } from '@/utils/error-utils';
import { LOGIN_INVITE_EXPIRED_PATH } from './login-notices';
import { Notice } from '@/components/ui/notice';

function isInviteExpiredError(error: unknown): boolean {
  return getAxiosErrorCode(error) === 'TOKEN_EXPIRED';
}

// Form validation schema
const passwordSetupSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(100, 'Password must be less than 100 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordSetupForm = z.infer<typeof passwordSetupSchema>;

// Loading component
function LoadingState() {
  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center">Processing Invitation</CardTitle>
          <CardDescription className="text-center">
            Please wait while we process your invitation...
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-muted-foreground border-t-transparent rounded-full"></div>
        </CardContent>
      </Card>
    </CenteredScrollPage>
  );
}

// Error component for invalid token
function InvalidTokenError() {
  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center text-destructive">Invalid Invitation</CardTitle>
          <CardDescription className="text-center">
            Invalid invitation link. Please contact your administrator.
          </CardDescription>
        </CardHeader>
      </Card>
    </CenteredScrollPage>
  );
}

// Component for already verified users
function VerifiedUserInvite({ 
  token, 
  inviteData, 
}: { 
  token: string; 
  inviteData: InviteCheckResult; 
}) {
  const {data: userInfo } = useUser();
  const navigate = useNavigate();
  const acceptVerifiedInviteMutation = useAcceptInvite();

  // Auto-accept if user is logged in
  useEffect(() => {
    if (userInfo && userInfo.account.email === inviteData.email) {
      acceptVerifiedInviteMutation.mutate({ token });
    }
  }, [userInfo, inviteData]);

  if (isInviteExpiredError(acceptVerifiedInviteMutation.error)) {
    return <Navigate to={LOGIN_INVITE_EXPIRED_PATH} replace />;
  }

  // Existing-account linking is not supported in this flow; acceptance is bound to invite email.
  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <Building className="h-12 w-12" />
          </div>
          <CardTitle className="text-center">Hub Invitation</CardTitle>
          <CardDescription className="text-center">
            {acceptVerifiedInviteMutation.isSuccess 
              ? `You have successfully joined ${inviteData.hubName}!` 
              : `You've been invited to join ${inviteData.hubName}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {!acceptVerifiedInviteMutation.isSuccess && (
              <div className="border rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4" />
                  <p className="text-sm"><strong>Email:</strong> {inviteData.email}</p>
                </div>
                {inviteData.firstName && inviteData.surname && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <p className="text-sm"><strong>Name:</strong> {inviteData.firstName} {inviteData.surname}</p>
                  </div>
                )}
              </div>
            )}
            
            {acceptVerifiedInviteMutation.isSuccess ? (
              <Notice tone="success" className="rounded p-4 text-base">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-success" />
                  <span className="font-medium">Invitation accepted successfully!</span>
                </div>
                {userInfo?.account.email === inviteData.email ? (
                  <p className="mt-2 text-sm">
                    Click the "Continue" button below to access your account with the new hub.
                  </p>
                ) : (
                  <p className="mt-2 text-sm">
                    Click the "Go to Login" button below to log in and access your account.
                  </p>
                )}
              </Notice>
            ) : (
              userInfo?.account.email === inviteData.email ? (
                <p className="text-sm text-center">
                  You are already logged in as <strong>{userInfo?.account.email}</strong>.
                  <br /><br />
                  Click "Accept Invitation" to join {inviteData.hubName}.
                </p>
              ) : (
                <p className="text-sm text-center">
                  Your account <strong>{inviteData.email}</strong> is already verified.
                  <br /><br />
                  Click "Accept Invitation" to join {inviteData.hubName}, then you can log in.
                </p>
              )
            )}
            
            {acceptVerifiedInviteMutation.isError && (
              <Notice tone="destructive" className="rounded p-3">
                <strong>Error:</strong> {acceptVerifiedInviteMutation.error?.message || 'Failed to accept invitation'}
              </Notice>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          {!acceptVerifiedInviteMutation.isSuccess && (
            <Button 
              className="flex-1" 
              onClick={() => acceptVerifiedInviteMutation.mutate({ token })}
              disabled={acceptVerifiedInviteMutation.isPending}
            >
              {acceptVerifiedInviteMutation.isPending ? 'Processing...' : 'Accept Invitation'}
            </Button>
          )}
          
          {userInfo?.account.email !== inviteData.email && acceptVerifiedInviteMutation.isSuccess && (
            <Button 
              className="flex-1"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          )}
          
          {acceptVerifiedInviteMutation.isSuccess && userInfo && (
            <Button 
              className="flex-1"
              onClick={() => navigate('/explore')}
            >
              Continue to Dashboard
            </Button>
          )}
        </CardFooter>
      </Card>
    </CenteredScrollPage>
  );
}

// Component for unverified users (password setup)
function UnverifiedUserInvite({ 
  token, 
  email 
}: { 
  token: string; 
  email: string; 
}) {
  const navigate = useNavigate();
  const acceptInviteMutation = useAcceptInvite();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordSetupForm>({
    resolver: zodResolver(passwordSetupSchema),
    mode: 'onBlur',
  });


  if (isInviteExpiredError(acceptInviteMutation.error)) {
    return <Navigate to={LOGIN_INVITE_EXPIRED_PATH} replace />;
  }

  // Show success state after password setup
  if (acceptInviteMutation.isSuccess) {
    return (
      <CenteredScrollPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-center">Account Verified!</CardTitle>
            <CardDescription className="text-center">
              Your account has been successfully verified. You can now log in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Notice tone="success" className="p-4">
                <h3 className="font-medium mb-2 text-center">Login Information</h3>
                
                {email && (
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4" />
                    <p className="text-sm font-medium">{email}</p>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  <p className="text-sm">Use the password you just created</p>
                </div>
              </Notice>
              
              <div className="border p-4 rounded">
                <p className="text-sm text-center">
                  Your account is ready! Click the button below to log in with your new credentials.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </CenteredScrollPage>
    );
  }

  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Accept Invitation</CardTitle>
          <CardDescription>
            Set your password to complete your account verification.
            {email && (
              <div className="mt-2">
                You're setting up an account for: <strong>{email}</strong>
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((data) => acceptInviteMutation.mutate({ token, password: data.password }))} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Set your password"
                {...register('password')}
                disabled={isSubmitting || acceptInviteMutation.isPending}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Confirm password"
                {...register('confirmPassword')}
                disabled={isSubmitting || acceptInviteMutation.isPending}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting || acceptInviteMutation.isPending}
            >
              {acceptInviteMutation.isPending ? 'Processing...' : 'Verify Account'}
            </Button>
          </form>
          
          {acceptInviteMutation.isError && (
            <Notice tone="destructive" className="mt-4 rounded p-3">
              <strong>Error:</strong> {acceptInviteMutation.error?.message || 'An error occurred'}
            </Notice>
          )}
        </CardContent>
      </Card>
    </CenteredScrollPage>
  );
}

// Main component that handles invite status checking
function InviteStatusHandler({ token }: { token: string }) {
  const {isLoading, isError, error, data} = useCheckInviteStatus(token);
  
  // Show loading state
  if (isLoading) {
    return <LoadingState />;
  }

  if (isInviteExpiredError(error)) {
    return <Navigate to={LOGIN_INVITE_EXPIRED_PATH} replace />;
  }

  // Show error state
  if (isError || !data) {
    return (
      <CenteredScrollPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Error</CardTitle>
            <CardDescription className="text-center">
              {isError && error?.message? error.message : 'Failed to load invitation details'}
            </CardDescription>
          </CardHeader>
        </Card>
      </CenteredScrollPage>
    );
  }



  // Show appropriate component based on verification status
  if (data.isVerified) {
    return (
      <VerifiedUserInvite 
        token={token} 
        inviteData={data} 
      />
    );
  }

  return (
    <UnverifiedUserInvite 
      token={token} 
      email={data.email} 
    />
  );
}

// Main wrapper component
export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Show error if no token
  if (!token) {
    return <InvalidTokenError />;
  }

  return <InviteStatusHandler token={token} />;
} 